import { z } from "zod";

import {
  AnalysedCaseContactSchema,
  CaseBaseSchema,
  DamageEntrySchema,
  DebtEntrySchema,
} from "./domain";

export const AccidentDynamicsEnum = z.enum([
  "hit_parked_or_stationary_vehicle",
  "both_maneuvering",
  "ran_stop_sign",
  "ran_red_light",
  "frontal_collision",
  "side_collision",
  "rear_collision",
  "illegal_turn",
  "chain_collision",
  "invaded_right_of_way",
  "reversing",
  "lane_change",
  "lost_control",
  "exited_parking_space_or_garage",
  "other",
]);
export type AccidentDynamics = z.infer<typeof AccidentDynamicsEnum>;

export const GuiltDegreeEnum = z.enum(["third_party", "concurrent", "claimant"]);
export type GuiltDegree = z.infer<typeof GuiltDegreeEnum>;

export const isLiabilitySolutionCompatible = (
  guiltDegree: z.infer<typeof GuiltDegreeEnum>,
  solution: string,
): boolean => guiltDegree !== "claimant" || solution === "close_process";

const LiabilityAnalysisFieldSchema = <T extends z.ZodType>(suggestionSchema: T) =>
  z
    .object({
      suggested: suggestionSchema.optional(),
      reasoning: z.string().optional().describe("Reasoning provided by AI"),
      reviewed: suggestionSchema.optional(),
    })
    .refine(({ suggested, reasoning }) => Boolean(suggested) === Boolean(reasoning));

export const LiabilityAnalysisSchema = z.object({
  accidentDynamics: LiabilityAnalysisFieldSchema(AccidentDynamicsEnum).optional(),
  guiltDegree: LiabilityAnalysisFieldSchema(GuiltDegreeEnum).optional(),
  updatedAt: z.iso.datetime(),
});

export const CaseAnalysisResultSchema = CaseBaseSchema.omit({
  caseType: true,
  externalCode: true,
  id: true,
  notes: true,
  clientId: true,
})
  .partial()
  .extend({
    contacts: z.array(AnalysedCaseContactSchema).optional(),
    damages: z
      .array(DamageEntrySchema.omit({ id: true }).required({ sourceDocumentId: true }))
      .optional(),
    debt: z
      .object({
        entries: z.array(DebtEntrySchema.omit({ id: true }).required({ sourceDocumentId: true })),
      })
      .optional()
      .describe("Debt collection line items; the prescription period is never extracted"),
    problematicDocumentIds: z
      .array(z.string())
      .optional()
      .describe("Document IDs the AI flagged as inconsistent with the rest of the case"),
    documentAnalysis: z
      .array(
        z.object({
          documentId: z.string(),
          note: z
            .string()
            .min(1)
            .max(400)
            .describe(
              "1-2 sentence note: what key data was extracted, any issues found, and for flagged documents, why they conflict with the rest of the case",
            ),
        }),
      )
      .optional()
      .describe("Per-document analysis notes"),
  });
